import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";

/* =========================
   WaterfallChart Component
   ========================= */

function buildWaterfallData(items, totalLabel = "Total", totalValue = null) {
  let running = 0;

  const steps = items.map((it) => {
    const base = running;
    running += Number(it.value) || 0;
    return { name: it.name, base, step: Number(it.value) || 0, isTotal: false };
  });

  const finalTotal = totalValue ?? running;
  steps.push({ name: totalLabel, base: 0, step: finalTotal, isTotal: true });

  return steps;
}

const euroFmt = new Intl.NumberFormat("en-GB");

function formatNumber(value) {
  return euroFmt.format(Math.round(Number(value) || 0));
}

export default function WaterfallChart({
  title = "Levelised cost of final products",
  yLabel = "Levelised cost of final products (EUR/tonne)",
  items = [], // [{ name, value }, ...]  (without Total)
  totalLabel = "Total",
  totalValue, // optional (if omitted, sum(items))
  height = 360,
  unit = "EURO/tonne",
  showTable = true,
}) {
  const data = buildWaterfallData(items, totalLabel, totalValue);
  const computedTotal =
    totalValue ?? items.reduce((sum, item) => sum + (Number(item.value) || 0), 0);

  return (
    <div className="rounded-xl border border-gray-300 bg-white p-6 shadow-sm mb-5" >
      <h2 className="text-2xl font-bold tracking-tight text-slate-900 md:text-2xl" >
        {title}
      </h2>

      <div style={{ width: "100%", height }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 20, right: 20, left: 10, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" interval={0} tick={{ fontSize: 12 }} />
            <YAxis
              tick={{ fontSize: 12 }}
              label={{
                value: yLabel,
                angle: -90,
                position: "insideLeft",
                style: { textAnchor: "middle" },
              }}
            />
            <Tooltip
              formatter={(val, key) => {
                if (key === "base") return [null, ""];
                return [formatNumber(val), unit];
              }}
            />

            <Bar dataKey="base" stackId="w" fill="rgba(0,0,0,0)" />

            <Bar dataKey="step" stackId="w">
              {data.map((entry, idx) => (
                <Cell
                  key={`wf-${idx}`}
                  fill={entry.isTotal ? "#9BBB59" : "#4F81BD"}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {showTable && (
        <div style={{ marginTop: 20, overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontFamily: "Arial, Helvetica, sans-serif",
              fontSize: 14,
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f3f4f6" }}>
                <th
                  style={{
                    border: "1px solid #d1d5db",
                    padding: "10px 12px",
                    textAlign: "left",
                    fontWeight: 600,
                  }}
                >
                  Category
                </th>
                <th
                  style={{
                    border: "1px solid #d1d5db",
                    padding: "10px 12px",
                    textAlign: "right",
                    fontWeight: 600,
                  }}
                >
                  Value
                </th>
                <th
                  style={{
                    border: "1px solid #d1d5db",
                    padding: "10px 12px",
                    textAlign: "left",
                    fontWeight: 600,
                  }}
                >
                  Unit
                </th>
              </tr>
            </thead>

            <tbody>
              {items.map((item, index) => (
                <tr key={`${item.name}-${index}`}>
                  <td
                    style={{
                      border: "1px solid #d1d5db",
                      padding: "10px 12px",
                      textAlign: "left",
                    }}
                  >
                    {item.name}
                  </td>
                  <td
                    style={{
                      border: "1px solid #d1d5db",
                      padding: "10px 12px",
                      textAlign: "right",
                    }}
                  >
                    {formatNumber(item.value)}
                  </td>
                  <td
                    style={{
                      border: "1px solid #d1d5db",
                      padding: "10px 12px",
                      textAlign: "left",
                    }}
                  >
                    {unit}
                  </td>
                </tr>
              ))}

              <tr style={{ backgroundColor: "#f9fafb", fontWeight: 700 }}>
                <td
                  style={{
                    border: "1px solid #d1d5db",
                    padding: "10px 12px",
                    textAlign: "left",
                  }}
                >
                  {totalLabel}
                </td>
                <td
                  style={{
                    border: "1px solid #d1d5db",
                    padding: "10px 12px",
                    textAlign: "right",
                  }}
                >
                  {formatNumber(computedTotal)}
                </td>
                <td
                  style={{
                    border: "1px solid #d1d5db",
                    padding: "10px 12px",
                    textAlign: "left",
                  }}
                >
                  {unit}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}